import { DateTime } from "luxon";
import {
  type Address,
  type JointAddress,
  type ProfileAddressType,
} from "../../../graphql/supergraphTypes";
import { AddressFormValues } from "../../IndividualForm/types";

/**
 * Builds the domicile address array accepted by the joint profile mutations.
 * Returns undefined when no domicile country is available.
 */
export const buildJointDomicileAddresses = (
  domicileCountry?: string | null,
): JointAddress[] | undefined =>
  domicileCountry
    ? [{ addressType: "primary_domicile", domicile: domicileCountry }]
    : undefined;

export const profileAddressToAddress = (profileAddress: ProfileAddressType): Address => {
  return {
    addressType: "domicile",
    city: profileAddress.city || undefined,
    country: profileAddress.country?.alpha2code || profileAddress.country?.name || undefined,
    postalCode: profileAddress.postalCode || undefined,
    province: profileAddress.province || undefined,
    state: profileAddress.state?.code || undefined,
    street: profileAddress.street || undefined,
    street2: profileAddress.street2 || undefined,
    street3: profileAddress.street3 || undefined,
    subprovince: profileAddress.subprovince || undefined,
  };
};

/**
 * Converts an array of ProfileAddressType to Address array
 */
export const profileAddressesToAddresses = (
  profileAddresses?: (ProfileAddressType | { [key: string]: unknown })[] | null,
): Address[] => {
  if (!profileAddresses) return [];

  return profileAddresses.map((addr) => {
    const profileAddress = addr as ProfileAddressType;
    return profileAddressToAddress(profileAddress);
  });
};

export const prepareAddressFromForm = (values: AddressFormValues) => ({
  street: values.streetLine1,
  street2: values.streetLine2 || null,
  city: values.city,
  state: values.state,
  province: values.province || null,
  postalCode: values.zip || null,
  country: values.country,
  addressType: "domicile",
});

/**
 * Helper to safely handle potentially `any` typed date values
 * @param date
 * @returns
 */
export const getSafeDate = (date: unknown): string | undefined => {
  if (typeof date === "string" && date.length > 0) {
    return date;
  }
  return undefined;
};

/**
 * Formats a date as a `yyyy-MM-dd` calendar date string using the date's
 * local components. Avoids `toISOString()`, which converts through UTC and
 * can shift the calendar day for users in timezones ahead of UTC.
 */
export const getProfileDate = (date?: Date | null): string | undefined => {
  if (!date) return undefined;
  return DateTime.fromJSDate(date).toFormat("yyyy-MM-dd");
};
